import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Shield, Users, ChevronLeft, Mail, Lock, Download, Share, Smartphone } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_a07fe825-978d-42af-ad0d-66b05bf66e9f/artifacts/6h7nm3ap_Photoroom_20260215_225825.png";
const API = "/api";

const StaffLogin = () => {
  const { initiateGoogleLogin, login } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("staff");
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showIosHelp, setShowIosHelp] = useState(false);

  const isIos = typeof navigator !== "undefined" && /iphone|ipad|ipod/i.test(navigator.userAgent);
  const isStandalone = typeof window !== "undefined" &&
    (window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone);

  useEffect(() => {
    // Point the install to the staff-specific manifest (opens straight to /staff-login)
    const manifestLink = document.querySelector('link[rel="manifest"]');
    const originalHref = manifestLink?.getAttribute("href");
    if (manifestLink) manifestLink.setAttribute("href", "/staff-manifest.json");

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => { setIsInstalled(true); setDeferredPrompt(null); };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      if (manifestLink && originalHref) manifestLink.setAttribute("href", originalHref);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") toast.success("Staff app added to your home screen!");
      setDeferredPrompt(null);
    } else if (isIos) {
      setShowIosHelp(true);
    } else {
      toast.info("Open your browser menu and choose 'Install app' or 'Add to Home Screen'.");
    }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password) {
      toast.error("Please enter email and password");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/auth/email-login`, {
        email: formData.email,
        password: formData.password,
      }, { withCredentials: true });

      // Check if user has staff/admin role
      if (response.data.role === "customer") {
        toast.error("This account is not registered as staff or admin. Please use the Customer Login.");
        return;
      }

      toast.success("Login successful!");
      window.location.href = response.data.role === "admin" ? "/dashboard" : "/staff-schedule";
    } catch (error) {
      console.error("Login failed:", error);
      toast.error(error.response?.data?.detail || "Login failed. Please check your credentials.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetEmailForm = () => {
    setShowEmailForm(false);
    setFormData({ email: "", password: "" });
  };

  const renderAuthOptions = (loginType) => {
    if (showEmailForm) {
      return (
        <div className="animate-fade-in">
          <div className="text-center mb-4">
            <h3 className="text-white font-semibold mb-1">Sign In with Email</h3>
            <p className="text-[#A1A1AA] text-sm">Enter your credentials</p>
          </div>

          <form onSubmit={handleEmailLogin} className="space-y-4">
            <div>
              <Label className="text-[#A1A1AA]">Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A1A1AA]" />
                <Input
                  data-testid={`${loginType}-email-input`}
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="pl-10 bg-[#0A0A0A] border-[#27272A] text-white focus:border-[#39FF14]"
                  placeholder="you@example.com"
                  required
                />
              </div>
            </div>

            <div>
              <Label className="text-[#A1A1AA]">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A1A1AA]" />
                <Input
                  data-testid={`${loginType}-password-input`}
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="pl-10 bg-[#0A0A0A] border-[#27272A] text-white focus:border-[#39FF14]"
                  placeholder="Your password"
                  required
                />
              </div>
            </div>

            <Button
              type="submit"
              data-testid={`${loginType}-email-submit-btn`}
              disabled={isSubmitting}
              className="w-full bg-[#39FF14] text-black hover:bg-[#32D711] font-bold py-6 rounded-full"
            >
              {isSubmitting ? (
                <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
              ) : (
                "Sign In"
              )}
            </Button>

            <button
              type="button"
              onClick={resetEmailForm}
              className="w-full text-[#A1A1AA] text-sm hover:text-white transition-colors"
            >
              ← Back to sign in options
            </button>
          </form>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {/* Google Sign In */}
        <Button
          data-testid={`${loginType}-google-login-btn`}
          onClick={() => initiateGoogleLogin(loginType)}
          className="w-full bg-white text-black hover:bg-gray-100 font-semibold py-6 rounded-full flex items-center justify-center gap-3"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </Button>

        {/* Apple Sign In */}
        <Button
          data-testid={`${loginType}-apple-login-btn`}
          onClick={() => initiateGoogleLogin(loginType)}
          className="w-full bg-black text-white border border-white/20 hover:bg-gray-900 font-semibold py-6 rounded-full flex items-center justify-center gap-3"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
          </svg>
          Continue with Apple
        </Button>

        <div className="relative my-4">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-[#27272A]"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-[#121212] text-[#A1A1AA]">or</span>
          </div>
        </div>

        {/* Email Sign In */}
        <Button
          data-testid={`${loginType}-email-login-btn`}
          onClick={() => setShowEmailForm(true)}
          variant="outline"
          className="w-full border-[#27272A] text-white hover:bg-[#27272A] font-semibold py-6 rounded-full flex items-center justify-center gap-3"
        >
          <Mail className="w-5 h-5" />
          Sign in with Email
        </Button>

        <div className="bg-[#0A0A0A] border border-[#27272A] rounded-xl p-3 mt-4">
          <p className="text-[#A1A1AA] text-xs text-center">
            Works with any email: Gmail, iCloud, Yahoo, Outlook, and more
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex flex-col">
      {/* Header */}
      <header className="p-4 md:px-12 border-b border-[#27272A]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src={LOGO_URL} 
              alt="Zig Zag Lawncare" 
              className="h-12 w-auto object-contain rounded-lg"
            />
          </div>
          <Link to="/">
            <Button
              variant="ghost"
              className="text-[#A1A1AA] hover:text-white hover:bg-[#27272A]"
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="font-heading text-3xl font-bold text-white uppercase mb-2">
              Staff & Admin Portal
            </h1>
            <p className="text-[#A1A1AA]">Sign in to access your dashboard</p>
          </div>

          <div className="bg-[#121212] border border-[#27272A] rounded-xl p-6">
            <Tabs 
              value={activeTab} 
              onValueChange={(v) => {
                setActiveTab(v);
                resetEmailForm();
              }} 
              className="w-full"
            >
              <TabsList className="grid w-full grid-cols-2 bg-[#0A0A0A] mb-6">
                <TabsTrigger 
                  value="staff" 
                  className="data-[state=active]:bg-[#39FF14] data-[state=active]:text-black font-semibold"
                >
                  <Users className="w-4 h-4 mr-2" />
                  Staff
                </TabsTrigger>
                <TabsTrigger 
                  value="admin" 
                  className="data-[state=active]:bg-[#39FF14] data-[state=active]:text-black font-semibold"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  Admin
                </TabsTrigger>
              </TabsList>

              <TabsContent value="staff" className="space-y-4">
                <div className="text-center py-2">
                  <div className="w-14 h-14 bg-[#064E3B] rounded-full flex items-center justify-center mx-auto mb-3">
                    <Users className="w-7 h-7 text-[#39FF14]" />
                  </div>
                  <h3 className="text-white font-semibold mb-1">Staff Login</h3>
                  <p className="text-[#A1A1AA] text-sm mb-4">
                    View your assigned jobs and update status
                  </p>
                </div>
                {renderAuthOptions("staff")}
              </TabsContent>

              <TabsContent value="admin" className="space-y-4">
                <div className="text-center py-2">
                  <div className="w-14 h-14 bg-[#39FF14] rounded-full flex items-center justify-center mx-auto mb-3">
                    <Shield className="w-7 h-7 text-black" />
                  </div>
                  <h3 className="text-white font-semibold mb-1">Admin Login</h3>
                  <p className="text-[#A1A1AA] text-sm mb-4">
                    Full access to manage everything
                  </p>
                </div>
                {renderAuthOptions("admin")}
              </TabsContent>
            </Tabs>
          </div>

          <p className="text-center text-[#A1A1AA] text-sm mt-6">
            First time? Contact your admin to be added as staff.
          </p>

          {/* Install as App (staff quick-access) */}
          {!isStandalone && !isInstalled && (
            <div className="mt-4 bg-[#121212] border border-[#39FF14]/30 rounded-xl p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-[#064E3B] rounded-lg flex items-center justify-center shrink-0">
                  <Smartphone className="w-5 h-5 text-[#39FF14]" />
                </div>
                <div>
                  <p className="text-white text-sm font-semibold">Install the Staff App</p>
                  <p className="text-[#A1A1AA] text-xs">Add to your home screen for one-tap login</p>
                </div>
              </div>
              <Button
                data-testid="install-staff-app-btn"
                onClick={handleInstall}
                className="w-full bg-[#39FF14] text-black hover:bg-[#32D711] font-bold rounded-full"
              >
                <Download className="w-4 h-4 mr-2" />
                Add to Home Screen
              </Button>

              {showIosHelp && (
                <div data-testid="ios-install-help" className="mt-3 bg-[#0A0A0A] border border-[#27272A] rounded-lg p-3 text-[#A1A1AA] text-xs space-y-1">
                  <p className="text-white font-semibold flex items-center gap-1.5"><Share className="w-3.5 h-3.5 text-[#39FF14]" /> On iPhone / iPad:</p>
                  <p>1. Tap the <strong>Share</strong> button in Safari</p>
                  <p>2. Scroll and tap <strong>"Add to Home Screen"</strong></p>
                  <p>3. Tap <strong>Add</strong> — the Zig Zag Staff app appears on your home screen</p>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default StaffLogin;
